import { glyphUrl, PLAQUE, LABEL } from './hud-theme';
import { ITEM_DEFS, itemLabel } from '../config/item-defs';
import { itemIconSrc, rarityFrameClass, esc, ICON_FALLBACK_ATTR } from './item-icon';
import { t, tp, getIntlLocale } from '../i18n/i18n';
/**
 * In-run carried-loot panel — the first frontend surface for
 * design/gdd/loot-inventory.md (drop) and design/gdd/insurance.md
 * (insure/uninsure). Unlike DebugHud (a pointer-events:none playtesting
 * instrument), this is real player-facing UI: interactive, Tailwind-styled
 * to match the Hub screen's visual language, mobile tap-target compliant.
 *
 * ## Collapsed by default (2026-09-07)
 *
 * User: *"I think it would be better not to display the whole game with the
 * full inventory open like this, showing all the loot I have collected.
 * Instead, we can show notifications about what was added to the inventory,
 * how much inventory space I have left, and similar information. Then, if
 * needed, I can click with the mouse to fully open the inventory."*
 *
 * Right, and it got worse rather than better on its own: the chest-density
 * pass took a run from about five chests to between fifteen and forty, so a
 * list that was four rows deep when this panel was written is now twenty, and
 * it was eating a third of the screen down the side the player fights on.
 *
 * What is left open is the one line the carry decision actually turns on —
 * how heavy you are and how much bag is left — as a bar you read at a glance
 * rather than a list you audit. The list is one click or one keypress away,
 * and `ui/loot-toast.ts` says what went in as it goes in, which is the half
 * of the list anyone was really reading.
 *
 * The summary is a `<button>`, not a div with a handler: it is the control
 * that opens the bag, so it should be reachable by keyboard and announce
 * itself as pressable without this file having to reimplement either.
 */

export interface CarriedItemView {
  itemId: string;
  itemDefId: string;
  weightKg: number;
  insured: boolean;
}

/** Above this share of capacity the weight readout warns; above `FULL` it alarms. */
const HEAVY_FRACTION = 0.75;
const FULL_FRACTION = 0.95;

export class LootPanel {
  private readonly root: HTMLDivElement;
  private readonly summaryEl: HTMLButtonElement;
  private readonly weightEl: HTMLSpanElement;
  private readonly barFillEl: HTMLDivElement;
  private readonly countEl: HTMLSpanElement;
  private readonly chevronEl: HTMLSpanElement;
  private readonly listEl: HTMLDivElement;
  private insuranceSlots = 0;
  private capacityKg = 0;
  private expanded = false;

  onDrop?: (itemId: string) => void;
  onInsure?: (itemId: string) => void;
  onUninsure?: (itemId: string) => void;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    // Anchors the tutorial's loot and insurance steps (engine/tutorial/).
    this.root.dataset.tutorial = 'loot';
    this.root.className = [
      'fixed', 'bottom-[15.5rem]', 'sm:bottom-3', 'right-3', 'z-[1000]',
      'w-[min(88vw,320px)]',
      PLAQUE, 'p-2.5', 'flex', 'flex-col', 'gap-2',
    ].join(' ');

    // ── The always-visible summary ──────────────────────────────────────
    this.summaryEl = document.createElement('button');
    this.summaryEl.type = 'button';
    this.summaryEl.dataset.hud = 'loot-summary';
    this.summaryEl.className = [
      'w-full', 'min-h-11', 'flex', 'flex-col', 'gap-1.5', 'text-left',
      'rounded', 'px-1', 'py-0.5', 'hover:bg-surface-2/60',
    ].join(' ');
    this.summaryEl.addEventListener('click', () => this.setExpanded(!this.expanded));

    const topRow = document.createElement('div');
    topRow.className = `font-heading text-[13px] tracking-wide flex items-center gap-1.5 ${LABEL}`;
    // The satchel makes the panel legible before it is read: what this corner
    // of the screen is about is "the bag you are carrying out".
    const bag = document.createElement('img');
    bag.src = glyphUrl('satchel');
    bag.alt = '';
    bag.draggable = false;
    bag.className = 'w-4 h-4 shrink-0 select-none';
    this.weightEl = document.createElement('span');
    this.weightEl.className = 'tabular-nums';
    this.countEl = document.createElement('span');
    this.countEl.className = 'ml-auto text-[11px] text-text-dim tabular-nums';
    this.chevronEl = document.createElement('span');
    this.chevronEl.className = 'text-[11px] text-text-dim select-none';
    topRow.append(bag, this.weightEl, this.countEl, this.chevronEl);

    // The bar is the part that gets read at a glance mid-fight. A number
    // tells you 19.7; a bar tells you "nearly full" without being read.
    const barTrack = document.createElement('div');
    barTrack.className = 'h-1.5 w-full rounded-full bg-surface-2 border border-border overflow-hidden';
    this.barFillEl = document.createElement('div');
    this.barFillEl.className = 'h-full bg-emerald-500 transition-[width] duration-200';
    barTrack.appendChild(this.barFillEl);

    this.summaryEl.append(topRow, barTrack);

    // ── The list, hidden until asked for ────────────────────────────────
    this.listEl = document.createElement('div');
    this.listEl.className = 'flex flex-col gap-1.5 max-h-[32vh] sm:max-h-[45vh] overflow-y-auto';
    this.listEl.hidden = true;

    this.root.append(this.summaryEl, this.listEl);
    container.appendChild(this.root);
    this.renderSummary(0, 0, 0);
  }

  setInsuranceSlots(slots: number): void {
    this.insuranceSlots = slots;
  }

  /** Whether the itemised list is showing. */
  get isExpanded(): boolean {
    return this.expanded;
  }

  setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    this.listEl.hidden = !expanded;
    this.chevronEl.textContent = expanded ? '▾' : '▸';
    this.summaryEl.setAttribute('aria-expanded', String(expanded));
  }

  toggle(): void {
    this.setExpanded(!this.expanded);
  }

  update(items: CarriedItemView[], carriedWeightKg: number, capacityKg?: number): void {
    if (capacityKg !== undefined && capacityKg > 0) this.capacityKg = capacityKg;
    const insuredCount = items.filter((i) => i.insured).length;
    this.renderSummary(carriedWeightKg, insuredCount, items.length);

    if (items.length === 0) {
      this.listEl.innerHTML = `<p class="text-[12px] text-text-dim">${esc(t('Nothing carried.'))}</p>`;
      return;
    }
    this.listEl.innerHTML = items.map((item) => `
      <div class="flex items-center justify-between gap-2 px-2 py-1.5 bg-surface-2 border border-border rounded">
        ${thumbMarkup(item)}
        <div class="min-w-0 flex-1">
          <p class="text-[13px] truncate">${esc(labelFor(item.itemDefId))}</p>
          <p class="text-[11px] ${item.insured ? 'text-gold' : 'text-text-dim'}">${esc(item.insured ? t('{kg}kg · insured', { kg: kg(item.weightKg) }) : t('{kg}kg', { kg: kg(item.weightKg) }))}</p>
        </div>
        <div class="flex gap-1 shrink-0">
          <button data-insure="${item.itemId}" class="min-h-11 min-w-11 px-2 bg-surface border border-border rounded text-[11px] hover:border-gold/50">
            ${esc(item.insured ? t('Uninsure') : t('Insure'))}
          </button>
          <button data-drop="${item.itemId}" class="min-h-11 min-w-11 px-2 bg-surface border border-border rounded text-[11px] text-error hover:border-error/60">
            ${esc(t('Drop'))}
          </button>
        </div>
      </div>
    `).join('');

    this.listEl.querySelectorAll<HTMLButtonElement>('button[data-drop]').forEach((btn) => {
      btn.addEventListener('click', () => this.onDrop?.(btn.dataset.drop!));
    });
    this.listEl.querySelectorAll<HTMLButtonElement>('button[data-insure]').forEach((btn) => {
      const itemId = btn.dataset.insure!;
      const isInsured = items.find((i) => i.itemId === itemId)?.insured ?? false;
      btn.addEventListener('click', () => (isInsured ? this.onUninsure?.(itemId) : this.onInsure?.(itemId)));
    });
  }

  /**
   * The one line that stays on screen.
   *
   * Weight against capacity rather than weight alone, because "19.7kg" only
   * means something next to the number it is running out of — and that number
   * is per-player (talent-trees.md's hauling build), which is why the server
   * sends it rather than the client assuming the base constant.
   *
   * The insured count carries the same shield the Sconce's Insurance panel
   * uses, so "protected" is one mark in both places rather than the word
   * "insured" here and a picture there.
   */
  private renderSummary(carriedWeightKg: number, insuredCount: number, itemCount: number): void {
    const capacity = this.capacityKg;
    const fraction = capacity > 0 ? Math.min(1, carriedWeightKg / capacity) : 0;
    const weight = capacity > 0
      ? t('{kg} / {cap}kg', { kg: kg(carriedWeightKg), cap: capacity.toLocaleString(getIntlLocale(), { maximumFractionDigits: 0 }) })
      : t('{kg}kg', { kg: kg(carriedWeightKg) });
    // Written out per branch, never interpolated: Tailwind v4 extracts class
    // names statically, so a template hole inside a class attribute produces
    // no CSS rule at all.
    let tone = 'tabular-nums';
    let fill = 'h-full bg-emerald-500 transition-[width] duration-200';
    if (fraction >= FULL_FRACTION) {
      tone = 'tabular-nums text-error';
      fill = 'h-full bg-red-500 transition-[width] duration-200';
    } else if (fraction >= HEAVY_FRACTION) {
      tone = 'tabular-nums text-gold';
      fill = 'h-full bg-amber-400 transition-[width] duration-200';
    }
    this.weightEl.className = tone;
    this.weightEl.textContent = weight;
    this.barFillEl.className = fill;
    this.barFillEl.style.width = `${(fraction * 100).toFixed(1)}%`;

    this.countEl.innerHTML = esc(tp(itemCount, '{n} item', '{n} items'))
      + `<span class="inline-flex items-center gap-1 ml-1.5 align-[-0.15em]">${SHIELD_GLYPH}`
      + `<span class="tabular-nums ${insuredCount > 0 ? 'text-gold' : ''}">${insuredCount}/${this.insuranceSlots}</span></span>`;
    this.chevronEl.textContent = this.expanded ? '▾' : '▸';
  }

  dispose(): void {
    this.root.remove();
  }
}

/** One decimal, in the player's own number format. */
function kg(value: number): string {
  return value.toLocaleString(getIntlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

// `itemLabel` and not `ITEM_DEFS` alone: worn gear rides the carried-item
// pipeline under its own table, and naming it from its id gave
// "Chandlers Crown", untranslated (QA 10.10.2026).
function labelFor(itemDefId: string): string {
  return itemLabel(itemDefId);
}

/** The Insurance panel's shield, inline and small, for the header's insured count. */
const SHIELD_GLYPH = '<img src="/assets/ui/insurance/shield.webp" alt="" aria-hidden="true"'
  + ' class="w-3.5 h-3.5 shrink-0 select-none" draggable="false" />';

/**
 * A thumbnail of what you are actually carrying (2026-09-02).
 *
 * The in-run bag listed loot by name while the Sconce's Stash showed the
 * object — the same item with two different faces depending on which side of
 * the extraction you were on. This is the Stash's own picture and rarity
 * frame, shrunk to fit a 320px overlay, plus a shield corner on anything
 * insured so "protected" is visible without reading the row.
 */
function thumbMarkup(item: CarriedItemView): string {
  const rarity = ITEM_DEFS[item.itemDefId]?.rarity ?? 'COMMON';
  const shield = item.insured
    ? `<span class="absolute -bottom-0.5 -right-0.5">${SHIELD_GLYPH}</span>`
    : '';
  return `<div class="relative shrink-0 w-9 h-9 rounded border ${rarityFrameClass(rarity)} overflow-hidden">
      <img ${ICON_FALLBACK_ATTR} src="${esc(itemIconSrc(item.itemDefId))}" alt="" aria-hidden="true" loading="lazy"
           class="absolute inset-0 w-full h-full object-contain p-0.5" />${shield}</div>`;
}
